import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { BucketCategory } from '@/features/evaluation/types';
import { getBucketCategory } from '@/features/evaluation/types';

// =========================================
// ScoreBar — color-coded progress bar
// =========================================

interface ScoreBarProps {
  score: number;
  maxScore?: number;
  label?: string;
  showPercentage?: boolean;
  showBucket?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_MAP = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
} as const;

const BUCKET_BG: Record<BucketCategory, string> = {
  include: 'bg-emerald-500',
  maybe: 'bg-amber-500',
  exclude: 'bg-rose-500',
};

const BUCKET_TEXT: Record<BucketCategory, string> = {
  include: 'text-emerald-700',
  maybe: 'text-amber-700',
  exclude: 'text-rose-700',
};

const BUCKET_LABEL: Record<BucketCategory, string> = {
  include: 'Include',
  maybe: 'Maybe',
  exclude: 'Exclude',
};

function getScoreColor(percentage: number): string {
  if (percentage >= 70) return BUCKET_BG.include;
  if (percentage >= 40) return BUCKET_BG.maybe;
  return BUCKET_BG.exclude;
}

function getScoreTextColor(percentage: number): string {
  if (percentage >= 70) return BUCKET_TEXT.include;
  if (percentage >= 40) return BUCKET_TEXT.maybe;
  return BUCKET_TEXT.exclude;
}

export function ScoreBar({
  score,
  maxScore = 100,
  label,
  showPercentage = true,
  showBucket = false,
  size = 'md',
  className,
}: ScoreBarProps) {
  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const clamped = Math.min(100, Math.max(0, percentage));
  const bucket = getBucketCategory(clamped);

  return (
    <div className={cn('space-y-1', className)}>
      {(label || showPercentage || showBucket) && (
        <div className="flex items-center justify-between text-sm">
          {label && (
            <span className="font-medium text-slate-700 truncate mr-2">
              {label}
            </span>
          )}
          <div className="flex items-center gap-2 shrink-0">
            {showBucket && (
              <span
                className={cn(
                  'text-xs font-medium px-1.5 py-0.5 rounded',
                  bucket === 'include' && 'bg-emerald-100 text-emerald-700',
                  bucket === 'maybe' && 'bg-amber-100 text-amber-700',
                  bucket === 'exclude' && 'bg-rose-100 text-rose-700'
                )}
              >
                {BUCKET_LABEL[bucket]}
              </span>
            )}
            {showPercentage && (
              <span className={cn('font-semibold tabular-nums', getScoreTextColor(clamped))}>
                {clamped}%
              </span>
            )}
          </div>
        </div>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'w-full rounded-full bg-slate-100 overflow-hidden',
              SIZE_MAP[size]
            )}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                getScoreColor(clamped)
              )}
              style={{ width: `${clamped}%` }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            Score: {score}/{maxScore} ({clamped}%)
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

// =========================================
// ScoreBadge — compact inline score display
// =========================================

interface ScoreBadgeProps {
  score: number;
  className?: string;
}

export function ScoreBadge({ score, className }: ScoreBadgeProps) {
  const bucket = getBucketCategory(score);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums',
        bucket === 'include' && 'bg-emerald-100 text-emerald-700',
        bucket === 'maybe' && 'bg-amber-100 text-amber-700',
        bucket === 'exclude' && 'bg-rose-100 text-rose-700',
        className
      )}
    >
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full',
          BUCKET_BG[bucket]
        )}
      />
      {score}%
    </span>
  );
}

// =========================================
// WeightedScoreBar — for criteria breakdown
// =========================================

interface WeightedScoreBarProps {
  rawScore: number;
  weight: number;
  maxWeighted: number;
  criteriaName: string;
  className?: string;
}

export function WeightedScoreBar({
  rawScore,
  weight,
  maxWeighted,
  criteriaName,
  className,
}: WeightedScoreBarProps) {
  const weighted = rawScore * weight;
  const percentage = maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : 0;

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600 truncate mr-2">{criteriaName}</span>
        <span className="text-slate-500 text-xs shrink-0">
          {rawScore.toFixed(1)} x{weight} = {weighted.toFixed(1)}/{maxWeighted}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            getScoreColor(percentage)
          )}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
    </div>
  );
}
